import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Database, Check, Cog } from "lucide-react"
import { useUpdateField } from "@/hooks/useResources"
import { useQueryClient } from "@tanstack/react-query"
import { useToast } from "@/hooks/useToast"
import { ResourceContainer, ResourceField } from "@/types/api"
import { useSemanticTypes } from "@/hooks/useResources"

interface ColumnsTabProps {
  fieldsData: ResourceField[] | undefined
  fieldsLoading: boolean
  resource: any
  selectedContainer: ResourceContainer | null
  onFieldUpdate: (
    fieldId: string,
    updates: {
      description: string
      display_name: string
      visibility: string
      data_type: string
      semantic_type: string
    }
  ) => void
  onFieldSettingsOpen: (field: ResourceField) => void
}

export function ColumnsTab({
  fieldsData,
  fieldsLoading,
  resource,
  selectedContainer,
  onFieldUpdate: _onFieldUpdate,
  onFieldSettingsOpen,
}: ColumnsTabProps) {
  const [editingFields, setEditingFields] = useState<
    Record<
      string,
      { description: string; display_name: string; visibility: string; data_type: string; semantic_type: string }
    >
  >({})
  const [changedFields, setChangedFields] = useState<Record<string, Set<string>>>({})

  const queryClient = useQueryClient()
  const { toast } = useToast()
  const updateFieldMutation = useUpdateField()
  const { data: semanticTypes = [] } = useSemanticTypes()

  const handleFieldChange = (
    fieldId: string,
    field: "description" | "display_name" | "visibility" | "data_type" | "semantic_type",
    value: string
  ) => {
    setEditingFields((prev) => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        [field]: value,
      },
    }))

    // Track which specific field was changed
    setChangedFields((prev) => ({
      ...prev,
      [fieldId]: new Set([...(prev[fieldId] || []), field]),
    }))
  }

  const handleSaveField = (fieldId: string) => {
    const fieldData = editingFields[fieldId]
    if (!fieldData) return

    updateFieldMutation.mutate(
      {
        resourceId: resource!.id,
        containerId: selectedContainer!.id,
        fieldId: fieldId,
        description: fieldData.description,
        display_name: fieldData.display_name,
        visibility: fieldData.visibility,
        data_type: fieldData.data_type,
        semantic_type: fieldData.semantic_type,
      },
      {
        onSuccess: () => {
          toast({
            title: "Field updated",
            description: "Field has been updated successfully.",
          })
          // Refetch the data to ensure consistency
          queryClient
            .invalidateQueries({
              queryKey: ["resources/container-fields", resource!.id, selectedContainer!.id],
            })
            .finally(() => {
              // Remove from editing state immediately to prevent flickering
              setEditingFields((prev) => {
                const newState = { ...prev }
                delete newState[fieldId]
                return newState
              })

              // Clear changed fields tracking
              setChangedFields((prev) => {
                const newState = { ...prev }
                delete newState[fieldId]
                return newState
              })
            })
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update field. Please try again.",
            variant: "destructive",
          })
        },
      }
    )
  }

  const isFieldEditing = (fieldId: string) => {
    return editingFields[fieldId] !== undefined
  }

  const isFieldChanged = (
    fieldId: string,
    fieldType: "description" | "display_name" | "visibility" | "data_type" | "semantic_type"
  ) => {
    return changedFields[fieldId]?.has(fieldType) || false
  }

  const getFieldValue = (
    field: ResourceField,
    fieldType: "description" | "display_name" | "visibility" | "data_type" | "semantic_type"
  ) => {
    if (isFieldEditing(field.id)) {
      return editingFields[field.id][fieldType] || field[fieldType] || ""
    }
    return field[fieldType] || ""
  }

  // Skeleton loader component for fields
  const FieldSkeleton = () => (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 flex items-center space-x-3">
                <div className="h-5 w-24 animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
                <div className="h-5 w-16 animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
                <div className="h-5 w-20 animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
              </div>
              <div className="mb-2 h-4 w-32 animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
              <div className="h-4 w-full animate-pulse rounded bg-gray-300 dark:bg-gray-600"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  if (fieldsLoading) {
    return <FieldSkeleton />
  }

  if ((fieldsData || []).length === 0) {
    const isAnalyzing = resource?.status === "analyzing"

    return (
      <div className="py-12 text-center">
        <Database className="mx-auto mb-4 h-12 w-12 text-gray-400" />
        <h3 className="mb-2 font-medium text-gray-900 dark:text-white">
          {isAnalyzing ? "Analyzing resource structure" : "No fields found"}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isAnalyzing ? (
            <span>
              We&apos;re currently analyzing your resource to discover its structure and fields. <br /> This may take a
              few moments.
            </span>
          ) : (
            "This table doesn't have any fields yet."
          )}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {/* Header Row - Hidden on mobile */}
      <div className="hidden grid-cols-12 items-center gap-4 rounded-lg bg-gray-50 p-3 text-sm font-medium text-gray-600 dark:bg-gray-900/50 dark:text-gray-400 lg:grid">
        <div className="col-span-3">Field Name</div>
        <div className="col-span-2">Semantic Type</div>
        <div className="col-span-2">Visibility</div>
        <div className="col-span-5">Description</div>
      </div>

      {(fieldsData || []).map((field) => (
        <div
          key={field.id}
          className="cursor-pointer space-y-3 rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-black/60 lg:grid lg:grid-cols-12 lg:items-start lg:gap-4 lg:space-y-0"
        >
          {/* Field Name */}
          <div className="lg:col-span-3 lg:pt-0">
            <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 lg:hidden">Field Name</div>
            <div className="relative">
              <Input
                value={getFieldValue(field, "display_name")}
                onChange={(e) => handleFieldChange(field.id, "display_name", e.target.value)}
                placeholder={field.field_name}
                className="h-8 text-sm font-medium"
              />
              {isFieldChanged(field.id, "display_name") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSaveField(field.id)}
                  className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                >
                  <Check className="h-4 w-4 stroke-gray-800 dark:stroke-gray-200" />
                </Button>
              )}
            </div>
            <div className="mx-2 mt-0.5 hidden text-xs text-gray-500 dark:text-gray-400 lg:block">
              {field.field_name}
            </div>
          </div>

          {/* Semantic Type */}
          <div className="lg:col-span-2 lg:pt-0">
            <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 lg:hidden">Semantic Type</div>
            <div className="relative">
              <Select
                value={getFieldValue(field, "semantic_type") || field.semantic_type || "no_semantic_type"}
                onValueChange={(value) => handleFieldChange(field.id, "semantic_type", value)}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue>
                    {(() => {
                      const currentValue =
                        getFieldValue(field, "semantic_type") || field.semantic_type || "no_semantic_type"
                      const selectedType = semanticTypes.find((type) => type.value === currentValue)
                      return selectedType ? selectedType.label : "No semantic type"
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-96">
                  {Object.entries(
                    semanticTypes.reduce(
                      (groups, type) => {
                        if (!groups[type.category]) {
                          groups[type.category] = []
                        }
                        groups[type.category].push(type)
                        return groups
                      },
                      {} as Record<string, typeof semanticTypes>
                    )
                  ).map(([category, types]) => (
                    <div key={category}>
                      <div className="cursor-pointer bg-gray-50 px-2 py-1.5 text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                        {category}
                      </div>
                      {types.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2 py-1">
                            <div className="flex flex-col">
                              <span className="font-medium">{type.label}</span>
                              <span className="text-xs text-gray-500">{type.description}</span>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              {isFieldChanged(field.id, "semantic_type") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSaveField(field.id)}
                  className="absolute right-8 top-1/2 z-10 h-6 w-6 -translate-y-1/2 p-0"
                >
                  <Check className="h-4 w-4 stroke-gray-800 dark:stroke-gray-200" />
                </Button>
              )}
            </div>
          </div>

          {/* Visibility */}
          <div className="lg:col-span-2 lg:pt-0">
            <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 lg:hidden">Visibility</div>
            <div className="relative">
              <Select
                value={getFieldValue(field, "visibility") || "show"}
                onValueChange={(value) => handleFieldChange(field.id, "visibility", value)}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="show">Show</SelectItem>
                  <SelectItem value="hide">Hide</SelectItem>
                </SelectContent>
              </Select>
              {isFieldChanged(field.id, "visibility") && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSaveField(field.id)}
                  className="absolute right-8 top-1/2 z-10 h-6 w-6 -translate-y-1/2 p-0"
                >
                  <Check className="h-4 w-4 stroke-gray-800 dark:stroke-gray-200" />
                </Button>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="lg:col-span-5 lg:pt-0">
            <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 lg:hidden">Description</div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input
                  value={getFieldValue(field, "description")}
                  onChange={(e) => handleFieldChange(field.id, "description", e.target.value)}
                  placeholder="Add a description for this field..."
                  className="h-8 text-sm"
                />
                {isFieldChanged(field.id, "description") && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleSaveField(field.id)}
                    className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                  >
                    <Check className="h-4 w-4 stroke-gray-800 dark:stroke-gray-200" />
                  </Button>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onFieldSettingsOpen(field)}
                className="h-8 w-8 flex-shrink-0 p-0"
              >
                <Cog className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
